import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ProviderEvent, VisionContext} from '../../models/vte';
import {VisionProviderDiagnostics, VisionProviderSubscription,} from '../../models/vision-provider-management';
import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';
import {VisionProviderSubscriptionApiService} from '../../services/vision-provider-management';
import {OperationsMetrics, OperationsMetricsService,} from '../../services/vte/operations-metrics.service';
import {VteApiService} from '../../services/vte/vte-api.service';
import {
    OperationalSectionShellComponent
} from '../operational-readiness/operational-section-shell/operational-section-shell.component';
import {
    OperationalStatusCardComponent
} from '../operational-readiness/operational-status-card/operational-status-card.component';
import {
    OperationalWarningCardComponent
} from '../operational-readiness/operational-warning-card/operational-warning-card.component';
import {
    OperationalStatusCardView,
    OperationalWarningView,
} from '../operational-readiness/operational-readiness.models';
import {
    BridgeReadinessRow,
    CameraReadinessRow,
    countReadyCards,
    DeviceHealthReadinessPayload,
    extractPaginatedResults,
    mapInfrastructureCards,
    mapInstallationCards,
    mapIntegrationServicesCards,
    mapOrganizationIdentityCards,
    mapVehicleProcessingCards,
    mapVisionPlatformCards,
} from '../operational-readiness/adapters';
import {OnboardingChecklist} from './organization-onboarding.types';

export type {OnboardingChecklist, OnboardingChecklistItem} from './organization-onboarding.types';

/**
 * Operational Readiness host panel.
 * Owns API loading and section mapping; shared components are presentation-only.
 * Platform-wide health lives on Super User /health (P22-14) — not embedded here.
 */
@Component({
    selector: 'app-organization-onboarding',
    standalone: true,
    imports: [
        CommonModule,
        OperationalSectionShellComponent,
        OperationalStatusCardComponent,
        OperationalWarningCardComponent,
    ],
    templateUrl: './organization-onboarding.component.html',
    styleUrl: './organization-onboarding.component.css',
})
export class OrganizationOnboardingComponent implements OnChanges {
    @Input() organizationId: number | null = null;
    /** From existing Organization API / parent-held org detail (`is_active`). */
    @Input() organizationActive: boolean | null = null;
    /** When embedded under Organization tabs, hide the redundant panel title. */
    @Input() hidePanelHeading = false;

    checklist: OnboardingChecklist | null = null;
    private bridges: BridgeReadinessRow[] | null = null;
    private cameras: CameraReadinessRow[] | null = null;
    private deviceHealth: DeviceHealthReadinessPayload | null = null;
    private visionContext: VisionContext | null = null;
    private providerDiagnostics: VisionProviderDiagnostics | null = null;
    private providerSubscriptions: VisionProviderSubscription[] | null = null;
    private operationsMetrics: OperationsMetrics | null = null;
    private latestProviderEvent: ProviderEvent | null = null;
    private providerEventsLoadFailed = false;

    identityCards: OperationalStatusCardView[] = [];
    infrastructureCards: OperationalStatusCardView[] = [];
    visionCards: OperationalStatusCardView[] = [];
    integrationCards: OperationalStatusCardView[] = [];
    vehicleProcessingCards: OperationalStatusCardView[] = [];
    installationCards: OperationalStatusCardView[] = [];
    loading = false;
    loadError = false;
    readonly isSuperUser: boolean;

    constructor(
        private apiService: ApiService,
        private vteApiService: VteApiService,
        private visionProviderSubscriptionApi: VisionProviderSubscriptionApiService,
        private operationsMetricsService: OperationsMetricsService,
        authService: AuthService,
    ) {
        this.isSuperUser = authService.isSuperUser();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organizationId']) {
            this.loadReadinessData();
            return;
        }
        if (changes['organizationActive'] && this.checklist) {
            this.remapSections();
        }
    }

    get panelVisible(): boolean {
        return this.isSuperUser && !!this.organizationId;
    }

    get identitySummary(): string {
        return this.sectionSummary(this.identityCards);
    }

    get infrastructureSummary(): string {
        return this.sectionSummary(this.infrastructureCards);
    }

    get visionSummary(): string {
        return this.sectionSummary(this.visionCards);
    }

    get integrationSummary(): string {
        return this.sectionSummary(this.integrationCards);
    }

    get vehicleProcessingSummary(): string {
        return this.sectionSummary(this.vehicleProcessingCards);
    }

    get installationSummary(): string {
        return this.sectionSummary(this.installationCards);
    }

    get loadErrorWarning(): OperationalWarningView | null {
        if (!this.loadError) {
            return null;
        }
        return {
            problem: 'Operational readiness data could not be loaded.',
            impact:
                'Organization identity, infrastructure, vision, integration, vehicle processing, and installation status are unavailable.',
            recommendedAction: 'Refresh the page or retry. If the issue persists, check API access for Super Users.',
            scope: 'organization',
        };
    }

    private sectionSummary(cards: OperationalStatusCardView[]): string {
        if (!cards.length) {
            return '';
        }
        return `${countReadyCards(cards)} of ${cards.length} ready`;
    }

    private loadReadinessData(): void {
        if (!this.isSuperUser || !this.organizationId) {
            this.resetState();
            this.loadError = false;
            return;
        }

        const orgId = Number(this.organizationId);
        this.loading = true;
        this.loadError = false;

        forkJoin({
            checklist: this.apiService.getOrganizationOnboardingChecklist(orgId),
            bridges: this.apiService.viewBridges(1, 100, '', orgId).pipe(
                catchError(() => of(null)),
            ),
            cameras: this.apiService.viewCameras(1, 100, '', orgId).pipe(
                catchError(() => of(null)),
            ),
            deviceHealth: this.apiService.getDeviceHealth(orgId).pipe(
                map((payload) => payload as DeviceHealthReadinessPayload),
                catchError(() => of(null)),
            ),
            vision: this.vteApiService.getVisionContext(orgId).pipe(
                catchError(() => of(null)),
            ),
            diagnostics: this.visionProviderSubscriptionApi.getDiagnostics().pipe(
                catchError(() => of(null)),
            ),
            subscriptions: this.visionProviderSubscriptionApi
                .listSubscriptions(undefined, false, 1, 100)
                .pipe(
                    map((response) => response.results || []),
                    catchError(() => of(null)),
                ),
            operationsMetrics: this.operationsMetricsService.loadMetrics(String(orgId)).pipe(
                catchError(() => of(null)),
            ),
            latestProviderEvent: this.vteApiService
                .listProviderEvents({
                    org_id: orgId,
                    page: 1,
                    page_size: 1,
                    ordering: '-received_at',
                })
                .pipe(
                    map((response) => ({
                        failed: false as const,
                        event: response.results?.[0] || null,
                    })),
                    catchError(() => of({failed: true as const, event: null})),
                ),
        }).subscribe({
            next: ({
                       checklist,
                       bridges,
                       cameras,
                       deviceHealth,
                       vision,
                       diagnostics,
                       subscriptions,
                       operationsMetrics,
                       latestProviderEvent,
                   }) => {
                this.checklist = checklist as OnboardingChecklist;
                this.bridges =
                    bridges === null
                        ? null
                        : extractPaginatedResults<BridgeReadinessRow>(bridges);
                this.cameras =
                    cameras === null
                        ? null
                        : extractPaginatedResults<CameraReadinessRow>(cameras);
                this.deviceHealth = deviceHealth;
                this.visionContext = vision;
                this.providerDiagnostics = diagnostics;
                this.providerSubscriptions = subscriptions;
                this.operationsMetrics = operationsMetrics;
                this.providerEventsLoadFailed = latestProviderEvent.failed;
                this.latestProviderEvent = latestProviderEvent.event;
                this.remapSections();
                this.loading = false;
            },
            error: () => {
                this.resetState();
                this.loading = false;
                this.loadError = true;
            },
        });
    }

    private remapSections(): void {
        if (!this.checklist) {
            return;
        }
        const items = this.checklist.items || [];
        this.identityCards = mapOrganizationIdentityCards(items, this.organizationActive);
        this.infrastructureCards = mapInfrastructureCards({
            bridges: this.bridges,
            cameras: this.cameras,
            checklistItems: items,
            deviceHealth: this.deviceHealth,
        });
        this.visionCards = mapVisionPlatformCards({
            visionContext: this.visionContext,
            cameras: this.cameras,
        });
        this.integrationCards = mapIntegrationServicesCards({
            organizationId: Number(this.organizationId),
            visionContext: this.visionContext,
            cameras: this.cameras,
            diagnostics: this.providerDiagnostics,
            subscriptions: this.providerSubscriptions,
        });
        this.vehicleProcessingCards = mapVehicleProcessingCards({
            visionContext: this.visionContext,
            metrics: this.operationsMetrics,
            latestProviderEvent: this.latestProviderEvent,
            providerEventsLoadFailed: this.providerEventsLoadFailed,
        });
        this.installationCards = mapInstallationCards(items);
    }

    private resetState(): void {
        this.checklist = null;
        this.bridges = null;
        this.cameras = null;
        this.deviceHealth = null;
        this.visionContext = null;
        this.providerDiagnostics = null;
        this.providerSubscriptions = null;
        this.operationsMetrics = null;
        this.latestProviderEvent = null;
        this.providerEventsLoadFailed = false;
        this.identityCards = [];
        this.infrastructureCards = [];
        this.visionCards = [];
        this.integrationCards = [];
        this.vehicleProcessingCards = [];
        this.installationCards = [];
    }
}
